import styled from "@emotion/styled";
import { IconArrowLeft } from "@kompare/kompare-ui-library";
import dayjs from "dayjs";
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import aircash from "src/assets/images/Aircash.png";
import card from "src/assets/images/card.png";
import keks from "src/assets/images/keks.png";
import { i18n, navigate, PaymentOptionEnum, useGeneralSoContext, useGetSo } from 'src/common';
import { useGetOffer } from "src/common/apiGraphql/queries/useGetOffer.query";
import { TravelDataType } from "src/common/apiGraphql/types/types";
import { Accordion, ArrowDownIcon, Button, Card, ContainerComponent, Flex, Loader, Stepper, Text, WsPayIframe } from 'src/components';
import config from "src/config";
import { colors } from 'src/theme';
import { AppRoutes } from "./_router/app.routes";
import { Helmet } from "react-helmet-async";

const TextStyle2 = styled(Text)({
  marginBottom: '24px'
});


const LogoStyle = styled.img({
  height: '24px'
});

const ButtonContainerStyle = styled(Flex)({
  marginTop: 48,
  marginBottom: 40
});

const PriceStyle = styled(Text)({
  marginRight: 16,
  marginBottom: -4
});

const CheckoutPage = () => {
  const [openPaymentMethodIndex, setOpenPaymentMethodIndex] = useState<number | null>(null);

  const { so, refetch } = useGetSo();
  const { setSoId, setOfferId, setSoInitialData, offerId, soId } = useGeneralSoContext();
  const { offer, refetch: refetchOffer } = useGetOffer();
  const { soId: soIdFromUrl, offerId: offerIdFromUrl } = useParams();

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
    if (offerIdFromUrl) {
      setOfferId(offerIdFromUrl);
    }
  }, [soIdFromUrl, offerIdFromUrl]);


  useEffect(() => {
    if (!soId || !offerId) {
      navigate(AppRoutes.WEBSHOP.STEP_1);
      return;
    }
    if (so) {
      refetch();
    }
    if (offer) {
      refetchOffer();
    }
  }, []);



  useEffect(() => {
    if (so) {
      setSoInitialData(so.productTypeSpecific?.travelData as TravelDataType);
    }
    if (so?.productTypeSpecific?.travelData?.insuranceStartDate) {
      const startDate = dayjs(so.productTypeSpecific.travelData.insuranceStartDate);
      const today = dayjs().startOf('day');
      if (startDate.isBefore(today) || startDate.isSame(today, 'day')) {
        navigate(AppRoutes.WEBSHOP.STEP_1);
      }
    }
  }, [so]);

  // Listen for messages from iframe (checkout failure page)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {

      if (event.origin !== config.REACT_APP_URL) return;

      if (event.data && event.data.type === 'CHECKOUT_FAILURE') {
        const { errors } = event.data.data;

        navigate(`${AppRoutes.WEBSHOP.CHECKOUT_FAILURE}?ErrorCodes=${encodeURIComponent(String(errors ?? ''))}`);
      }
    };

    // Add event listener
    window.addEventListener('message', handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!offerId) return null;

  const paymentOptions = [
    {
      key: 'creditCard',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.onetimeCard'),
      icon: <LogoStyle src={card} alt="" />,
      content: <WsPayIframe offerId={offerId!} paymentOption={PaymentOptionEnum.CreditCard} paymentType='creditCard' title='Plaćanje karticom (WSPay)' />,
      price: offer?.productTypeSpecific?.price.totalPrice,
      brands: ['grawe', 'uniqa', 'sava']
    },
    {
      key: 'keksPay',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.keksPay'),
      icon: <LogoStyle src={keks} alt="" />,
      content: config.REACT_APP_IS_DEV === 'true' ? 'content' : <WsPayIframe offerId={offerId} paymentOption={PaymentOptionEnum.KeksPay} paymentType='keksPay' title='Plaćanje putem KeksPay (WSPay)' />,
      price: offer?.productTypeSpecific?.price.totalPrice,
      brands: ['uniqa', 'sava']
    },
    {
      key: 'aircash',
      type: 'oneTime',
      title: i18n.t('pages.checkoutPage.aircash'),
      icon: <LogoStyle src={aircash} alt="" />,
      content: config.REACT_APP_IS_DEV === 'true' ? 'content' : <WsPayIframe offerId={offerId} paymentOption={PaymentOptionEnum.Aircash} paymentType='aircash' title='Plaćanje putem Aircash (WSPay)' />,
      price: offer?.productTypeSpecific?.price.totalPrice,
      brands: ['uniqa']
    }
  ];


  const handleOpenPaymentMethod = (index: number) => {
    setOpenPaymentMethodIndex(openPaymentMethodIndex === index ? null : index);
  };

  const handleBackButton = () => {
    navigate(`${AppRoutes.WEBSHOP.STEP_4}`);
  }

  return (
    <ContainerComponent>
      <Helmet>
        <title>Plaćanje | Putno osiguranje - kompare.hr</title>
      </Helmet>
      <Flex vertical gap={24}>
        <Stepper currentStep={5} totalSteps={5} />
        {(offer && so) && (
          <Flex vertical gap={32}>
            <div>
              <Text as="h1" type="xLarge" bold>{i18n.t('pages.checkoutPage.title')}</Text>
              <TextStyle2 type='tiny'>{i18n.t('pages.checkoutPage.subtitle')}</TextStyle2>
              <Flex vertical gap={12}>
                {paymentOptions.map((pm, index) => {
                  if (!pm.brands.includes(offer?.product?.brand?.code)) {
                    return null;
                  }
                  return (
                    <Card padding={16} radius={12} key={index}>
                      <Accordion
                        key={index}
                        title={<Flex justify='space-between' align='center'>
                          <Flex gap={16} align='center'>{pm.icon}<Text as='span' type='base' bold>{pm.title}</Text></Flex><PriceStyle as='span' type='small' color={colors.text3}>{pm.price ? `${pm.price.toFixed(2)} €` : ''}</PriceStyle>
                        </Flex>}
                        headingLevel={2}
                        content={pm.content}
                        open={openPaymentMethodIndex === index}
                        expandable={true}
                        expandIcon={true}
                        expandIconComponent={<ArrowDownIcon color={colors.icon1} />}
                        gap={20}
                        onChange={() => handleOpenPaymentMethod(index)}
                        isLazy={true}
                      />
                    </Card>
                  )
                })}

              </Flex>
              <ButtonContainerStyle justify="center" align="center">
                <Button btnType="textBlack" iconPosition="left" text={i18n.t('pages.checkoutPage.backButton')} onClick={handleBackButton}>{<IconArrowLeft />}</Button>
              </ButtonContainerStyle>
            </div>
          </Flex>
        )}
        {(!offer || !so) && <Loader />}
      </Flex>
    </ContainerComponent>
  );
}

export default CheckoutPage;
